/**
 * V2AdminConnectorsPage.jsx — Admin → Connectors (/admin/connectors)
 * ─────────────────────────────────────────────────────────────────────────
 * Every integration is a preconfigured card driven by connectors.js. Col
 * clicks Connect, completes the service's login/key, and the pill flips to
 * Connected — no config files, no .env. Cards are grouped by category and
 * read their live status from GET /api/admin/connectors.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { V2OSChrome, V2OSC as C } from './V2AdminOSPage';
import { CONNECTORS, CONNECTOR_CATEGORIES, STATUS_META } from './connectors';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('kindra-admin-token') || ''}`,
});
const req = async (method, path, body) => {
  const r = await fetch(`${API}${path}`, {
    method, headers: authHeaders(), ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(d.detail || `HTTP ${r.status}`), { status: r.status });
  return d;
};

const ACCENT = 'var(--tt-orange)';

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.disconnected;
  return (
    <span data-testid={`connector-status-${status}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
      fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.02em', color: m.color,
      background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.hair}`,
      borderRadius: 999, padding: '3px 10px',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.dot,
        boxShadow: status === 'connected' ? `0 0 8px ${m.dot}` : 'none' }} />
      {m.label}
    </span>
  );
};

const btn = (variant) => ({
  fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, fontWeight: 700,
  borderRadius: 999, padding: '8px 16px', cursor: 'pointer', border: '1px solid transparent',
  ...(variant === 'primary'
    ? { background: 'linear-gradient(135deg,#F79640,#EC7C24)', color: '#fff', boxShadow: '0 6px 18px -8px rgba(236,124,36,0.7)' }
    : variant === 'ghost'
    ? { background: 'transparent', color: C.muted, border: `1px solid ${C.hair}` }
    : { background: 'transparent', color: '#F27A7A', border: '1px solid rgba(242,122,122,0.4)' }),
});

const inputStyle = {
  width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)',
  border: `1px solid ${C.hair}`, borderRadius: 10, padding: '9px 12px',
  fontFamily: 'Source Sans 3, sans-serif', fontSize: 13, color: C.icy, outline: 'none',
  marginTop: 8,
};

function ConnectorCard({ c, state, onChanged, showToast }) {
  const status = state?.status || 'disconnected';
  const connected = status === 'connected';
  const [open, setOpen] = useState(false);   // inline key/webhook form
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  const doOauth = async () => {
    setBusy(true);
    try {
      const d = await req('POST', c.connectPath);
      if (d.authUrl) { window.location.assign(d.authUrl); return; }
      showToast(`${c.name}: no auth URL returned — is the connector configured?`);
    } catch (e) {
      showToast(`${c.name} connect failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  const submitForm = async () => {
    setBusy(true);
    try {
      await req('POST', c.connectPath, vals);
      showToast(`${c.name} connected ✓`);
      setOpen(false); setVals({}); onChanged();
    } catch (e) {
      showToast(`${c.name} connect failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await req('POST', `/api/admin/connectors/${c.id}/disconnect`);
      showToast(`${c.name} disconnected`);
      onChanged();
    } catch (e) {
      showToast(`${c.name} disconnect failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  const onConnectClick = () => {
    if (c.authType === 'oauth') doOauth();
    else setOpen((o) => !o); // apikey / webhook_token reveal inline form
  };

  return (
    <div data-testid={`connector-card-${c.id}`} className="tt-glass" style={{
      padding: 16, display: 'flex', flexDirection: 'column', gap: 10,
      background: 'linear-gradient(158deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
      border: `1px solid ${C.hair}`, borderRadius: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: 'grid',
          placeItems: 'center', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.hair}` }}>
          <img src={c.logo} alt={c.name} style={{ width: 24, height: 24, objectFit: 'contain' }} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontFamily: 'Fredoka, sans-serif', fontSize: 15.5, fontWeight: 600, color: C.icy }}>{c.name}</h3>
            <StatusPill status={status} />
          </div>
          <p style={{ margin: '3px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12.5, color: C.muted, lineHeight: 1.4 }}>
            {c.blurb}{c.readOnly ? ' · read-only' : ''}
          </p>
          {connected && (state?.account_label || state?.last_synced_at) && (
            <p style={{ margin: '6px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: C.faint }}>
              {state.account_label || 'Connected'}{state.last_synced_at ? ` · synced ${state.last_synced_at}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Inline form for apikey / webhook_token */}
      {open && !connected && (
        <div data-testid={`connector-form-${c.id}`} style={{ borderTop: `1px solid ${C.hair}`, paddingTop: 10 }}>
          {c.authType === 'webhook_token' && (
            <div style={{ marginBottom: 4 }}>
              <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: C.muted }}>
                In the Fieldy app → Settings → Developer → Webhook Endpoint URL, paste:
              </p>
              <code style={{ display: 'block', marginTop: 6, padding: '8px 10px', borderRadius: 8,
                background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.hair}`, color: C.icy,
                fontSize: 12, wordBreak: 'break-all' }}>
                {(API || window.location.origin) + c.webhookPath}
              </code>
              <input style={inputStyle} placeholder="Webhook token (from Fieldy)" type="password"
                onChange={(e) => setVals({ token: e.target.value })} />
            </div>
          )}
          {c.authType === 'apikey' && (c.keyFields || []).map((f) => (
            <input key={f.name} style={inputStyle} placeholder={f.label} type={f.secret ? 'password' : 'text'}
              onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))} />
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button data-testid={`connector-save-${c.id}`} onClick={submitForm} disabled={busy} style={btn('primary')}>
              {busy ? 'Saving…' : 'Save & connect'}
            </button>
            <button onClick={() => setOpen(false)} style={btn('ghost')}>Cancel</button>
          </div>
        </div>
      )}

      {/* Action row */}
      {!open && (
        <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
          {connected ? (
            <>
              <button data-testid={`connector-reconnect-${c.id}`} onClick={onConnectClick} disabled={busy} style={btn('ghost')}>Reconnect</button>
              <button data-testid={`connector-disconnect-${c.id}`} onClick={disconnect} disabled={busy} style={btn('danger')}>Disconnect</button>
            </>
          ) : (
            <button data-testid={`connector-connect-${c.id}`} onClick={onConnectClick} disabled={busy} style={btn('primary')}>
              {busy ? '…' : `Connect ${c.name}`}
            </button>
          )}
          {c.docsUrl && (
            <a href={c.docsUrl} target="_blank" rel="noreferrer" style={{ ...btn('ghost'), textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Docs ↗</a>
          )}
        </div>
      )}
    </div>
  );
}

export default function V2AdminConnectorsPage() {
  const [byId, setById] = useState({});
  const [loadErr, setLoadErr] = useState(null);
  const [toast, setToast] = useState('');
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 5000); };

  const load = useCallback(async () => {
    try {
      const d = await req('GET', '/api/admin/connectors');
      const map = {};
      (d.connectors || []).forEach((x) => { map[x.id] = x; });
      setById(map); setLoadErr(null);
    } catch (e) {
      // Honest "not connected" — never fake it. Show all cards as disconnected.
      setById({}); setLoadErr(e.message);
    }
  }, []);
  useEffect(() => {
    // Auth guard — same pattern as V2DeskPage. Without a token every /api call
    // returns 401 "unauthenticated" and the page renders as "not reachable".
    if (!localStorage.getItem('kindra-admin-token')) {
      window.location.assign('/admin/login?returnTo=%2Fadmin%2Fconnectors');
      return;
    }
    load();
  }, [load]);

  return (
    <V2OSChrome activeTab="connectors">
      <div data-testid="os-connectors-page" style={{ maxWidth: 980, margin: '0 auto', width: '100%' }}>
        <p style={{ fontFamily: 'var(--ff-caps)', color: C.faint, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
          Admin · Connectors
        </p>
        <h1 style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy,
          fontSize: 'clamp(26px,4vw,36px)', lineHeight: 1.1, marginTop: 6, marginBottom: 8 }}>
          Plug in your <span style={{ color: ACCENT, fontStyle: 'italic', fontWeight: 400 }}>tools</span>
        </h1>
        <p style={{ color: C.muted, fontSize: 14.5, marginBottom: 8, maxWidth: 640 }}>
          Connect a service once and its data flows straight into the tiles. No config files, no .env —
          click Connect, sign in, done. A tile with nothing connected says so honestly; it never shows a fake $0.
        </p>
        {loadErr && (
          <p data-testid="connectors-load-note" style={{ color: C.faint, fontSize: 12, marginBottom: 12 }}>
            Showing everything as not-connected — the connectors service isn't reachable yet.
          </p>
        )}

        {CONNECTOR_CATEGORIES.map((cat) => {
          const items = CONNECTORS.filter((c) => c.category === cat.key);
          if (!items.length) return null;
          return (
            <section key={cat.key} data-testid={`connector-group-${cat.key}`} style={{ marginTop: 26 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontFamily: 'var(--ff-caps)', fontSize: 13, letterSpacing: '0.16em',
                  textTransform: 'uppercase', color: ACCENT }}>{cat.label}</h2>
                <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: C.faint }}>{cat.blurb}</span>
              </div>
              <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {items.map((c) => (
                  <ConnectorCard key={c.id} c={c} state={byId[c.id]} onChanged={load} showToast={showToast} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {toast && (
        <div data-testid="connectors-toast" style={{ position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)',
          zIndex: 90, background: 'rgba(23,24,27,0.96)', border: `1px solid ${C.hair}`, borderRadius: 12,
          padding: '10px 18px', color: C.icy, fontFamily: 'Source Sans 3, sans-serif', fontSize: 13,
          boxShadow: '0 12px 30px rgba(0,0,0,0.5)' }}>
          {toast}
        </div>
      )}
    </V2OSChrome>
  );
}
